<template>
  <!-- Partners: tools we recommend alongside AusProxies (separate from the FAQ). Links and affiliate code in helpers/partners.js. -->
  <BreadCrumb :PageTitle="t('partnersPage.title')" />
  <section v-if="user" class="pp-partners">
    <header class="pp-partners__hero">
      <h1>{{ t('partnersPage.title') }}</h1>
      <p>{{ t('partnersPage.intro') }}</p>
    </header>

    <article class="pp-partner">
      <div class="pp-partner__card">
        <span class="pp-partner__mark" aria-hidden="true">U</span>
        <div class="pp-partner__who">
          <div class="pp-partner__name">{{ UNDETECTABLE.name }} <span class="pp-partner__tag">{{ t('partnersPage.recommended') }}</span></div>
          <p>{{ t('partnersPage.undetectableLine') }}</p>
        </div>
        <div class="pp-partner__cta">
          <a class="pp-btn pp-btn--main" :href="UNDETECTABLE.url" target="_blank" rel="sponsored noopener">{{ t('partnersPage.try') }} ↗</a>
          <a class="pp-btn" :href="UNDETECTABLE.guideUrl" target="_blank" rel="sponsored noopener">{{ t('partnersPage.guide') }} ↗</a>
        </div>
      </div>

      <div class="pp-partner__body">
        <nav class="pp-toc" :aria-label="t('faqPage.onThisPage')">
          <b>{{ t('faqPage.onThisPage') }}</b>
          <ol>
            <li v-for="s in content.toc" :key="s.id"><a :href="'#' + s.id" @click.prevent="jump(s.id)">{{ s.title }}</a></li>
          </ol>
        </nav>
        <div class="faq-prose" v-html="content.html"></div>
      </div>

      <div class="pp-partner__foot">
        <span>{{ t('partnersPage.ready') }}</span>
        <a class="pp-btn pp-btn--main" :href="UNDETECTABLE.url" target="_blank" rel="sponsored noopener">{{ t('partnersPage.try') }} ↗</a>
      </div>
    </article>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import BreadCrumb from '../components/Common/BreadCrumb.vue';
import { useAccountStore } from '@/stores';
import { t } from '@/i18n';
import { UNDETECTABLE } from '@/helpers/partners';
import { UNDETECTABLE_PAGE } from '@/helpers/partner-content';
import { renderFaq } from '@/helpers/faq-format';
import '@/assets/css/faq.css';

const { user } = storeToRefs(useAccountStore());
const content = computed(() => renderFaq(UNDETECTABLE_PAGE, 'undetectable'));
const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
</script>

<style scoped>
.pp-partners { max-width: 1080px; margin: 0 auto 32px; color: var(--ui-ink); display: grid; gap: 18px; }
.pp-partners__hero { padding: 22px 24px; border: 1px solid var(--ui-line); border-radius: 16px; background: var(--ui-card); }
.pp-partners__hero h1 { margin: 0 0 6px; font-size: 28px; font-weight: 800; color: var(--ui-ink); }
.pp-partners__hero p { margin: 0; max-width: 62ch; color: var(--ui-dim); font-size: 15px; }
.pp-partner { border: 1px solid rgba(46, 196, 160, 0.45); border-radius: 16px; background: var(--ui-card); overflow: hidden; }
.pp-partner__card { display: flex; flex-wrap: wrap; align-items: center; gap: 16px 18px; padding: 20px 22px; background: linear-gradient(90deg, rgba(46, 196, 160, 0.14), rgba(101, 96, 240, 0.1)); border-bottom: 1px solid var(--ui-line); }
.pp-partner__mark { display: grid; place-items: center; flex: none; width: 56px; height: 56px; border-radius: 14px; background: #0f1720; color: #2ec4a0; font-size: 22px; font-weight: 800; }
.pp-partner__who { flex: 1; min-width: 240px; }
.pp-partner__name { font-size: 22px; font-weight: 800; }
.pp-partner__who p { margin: 4px 0 0; color: var(--ui-dim); font-size: 14.5px; max-width: 60ch; }
.pp-partner__tag { display: inline-block; margin-left: 6px; padding: 1px 9px; border-radius: 999px; background: rgba(46, 196, 160, 0.18); color: #7fe3c8; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; vertical-align: 4px; }
body:not(.dark) .pp-partner__tag { color: #0f6e57; }
.pp-partner__cta { display: flex; flex-wrap: wrap; gap: 8px; }
.pp-btn { display: inline-flex; align-items: center; padding: 9px 16px; border: 1px solid var(--ui-line-strong); border-radius: 10px; color: var(--ui-ink) !important; font-size: 14px; font-weight: 700; text-decoration: none; }
.pp-btn:hover { border-color: var(--ui-pri); }
.pp-btn--main { border-color: #2ec4a0; background: #2ec4a0; color: #0b1b16 !important; }
.pp-btn--main:hover { background: #3fd6b1; border-color: #3fd6b1; }
.pp-partner__body { display: grid; grid-template-columns: 220px minmax(0, 1fr); gap: 28px; padding: 20px 22px; }
@media (max-width: 900px) { .pp-partner__body { grid-template-columns: minmax(0, 1fr); } .pp-toc { position: static !important; } }
.pp-toc { position: sticky; top: 90px; align-self: start; padding: 14px; border: 1px solid var(--ui-line); border-radius: 12px; background: var(--ui-page); }
.pp-toc b { display: block; margin-bottom: 8px; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--ui-dim); }
.pp-toc ol { margin: 0; padding: 0; list-style: none; display: grid; gap: 2px; counter-reset: toc; }
.pp-toc li { counter-increment: toc; }
.pp-toc a { display: flex; gap: 8px; padding: 5px 6px; border-radius: 6px; color: var(--ui-ink); font-size: 13.5px; line-height: 1.35; text-decoration: none; }
.pp-toc a::before { content: counter(toc, decimal-leading-zero); flex: none; padding-top: 2px; font-size: 11px; color: var(--ui-dim); font-variant-numeric: tabular-nums; }
.pp-toc a:hover { background: var(--ui-pri-soft); }
.pp-partner__foot { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; padding: 14px 22px; border-top: 1px solid var(--ui-line); background: var(--ui-page); font-weight: 600; }
</style>
